import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import WamEmptyState from "./WamEmptyState";
import WamSectionCard from "./WamSectionCard";
import WamStatusBadge from "./WamStatusBadge";
import {
  buildAutopilotDashboardUrl,
  getAutopilotModuleLabel,
  normalizeAutopilotModule,
} from "./autopilotModuleMeta";


const GROUP_ORDER = ["reclamos", "gastos", "cobranzas", "conciliacion", "liquidaciones"];

const buildReviewGroups = (overview) => {
  const groups = new Map();
  const ensureGroup = (module) => {
    const normalized = normalizeAutopilotModule(module) || "otros";
    if (!groups.has(normalized)) {
      groups.set(normalized, {
        module: normalized,
        suggestions: [],
        approvals: [],
        risks: [],
      });
    }
    return groups.get(normalized);
  };

  (overview?.recent_actions || [])
    .filter((item) => item?.status !== "executed")
    .forEach((item) => {
      ensureGroup(item?.module).suggestions.push(item);
    });

  (overview?.pending_approvals || []).forEach((approval) => {
    if (!approval?.action) return;
    ensureGroup(approval.action.module).approvals.push(approval.action);
  });

  (overview?.risks || []).forEach((risk) => {
    ensureGroup(risk?.module).risks.push(risk);
  });

  return [...groups.values()].sort((left, right) => {
    const leftIndex = GROUP_ORDER.indexOf(left.module);
    const rightIndex = GROUP_ORDER.indexOf(right.module);
    if (leftIndex === -1 && rightIndex === -1) return left.module.localeCompare(right.module);
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  });
};

const WamAutopilotReviewDrawer = ({
  isOpen,
  onClose,
  overview,
  loading = false,
}) => {
  const surfaceBg = useColorModeValue("white", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const reviewGroups = useMemo(() => buildReviewGroups(overview), [overview]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent bg={surfaceBg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="wam.border">
          <Stack spacing={1}>
            <Text fontWeight="800">Autopilot pendiente</Text>
            <Text fontSize="sm" color={mutedText}>
              Revisá acciones, aprobaciones y riesgos sin salir del módulo actual.
            </Text>
          </Stack>
        </DrawerHeader>
        <DrawerBody py={4}>
          <Skeleton isLoaded={!loading}>
            {reviewGroups.length ? (
              <Stack spacing={4}>
                {reviewGroups.map((group) => {
                  const total = group.suggestions.length + group.approvals.length + group.risks.length;
                  return (
                    <WamSectionCard
                      key={group.module}
                      title={`Autopilot de ${getAutopilotModuleLabel(group.module)}`}
                      subtitle={`${total} elementos pendientes para revisar`}
                      actions={
                        <Button
                          as={RouterLink}
                          to={buildAutopilotDashboardUrl(group.module)}
                          size="sm"
                          colorScheme="blue"
                          onClick={onClose}
                        >
                          Abrir módulo
                        </Button>
                      }
                    >
                      <Stack spacing={3}>
                        <HStack spacing={2} flexWrap="wrap">
                          <WamStatusBadge label={`Sugerencias: ${group.suggestions.length}`} colorScheme="blue" />
                          <WamStatusBadge label={`Aprobaciones: ${group.approvals.length}`} colorScheme="orange" />
                          <WamStatusBadge label={`Riesgos: ${group.risks.length}`} colorScheme="red" />
                        </HStack>

                        {group.suggestions.slice(0, 2).map((action) => (
                          <Box key={`suggestion-${action.id}`} borderWidth="1px" borderColor="wam.border" borderRadius="16px" p={3}>
                            <Text fontWeight="700">{action.title || "Sugerencia de Autopilot"}</Text>
                            <Text fontSize="sm" color={mutedText}>
                              {action.description || action.reasoning || "Requiere revisión desde el dashboard del módulo."}
                            </Text>
                          </Box>
                        ))}

                        {group.approvals.slice(0, 2).map((action) => (
                          <Box key={`approval-${action.id}`} borderWidth="1px" borderColor="wam.border" borderRadius="16px" p={3}>
                            <Text fontWeight="700">{action.title || "Aprobación pendiente"}</Text>
                            <Text fontSize="sm" color={mutedText}>
                              {action.description || action.reasoning || "Quedó pendiente de aprobación manual."}
                            </Text>
                          </Box>
                        ))}

                        {group.risks.slice(0, 2).map((risk) => (
                          <Box key={`risk-${risk.id}`} borderWidth="1px" borderColor="wam.border" borderRadius="16px" p={3}>
                            <Text fontWeight="700">{risk.title || "Riesgo abierto"}</Text>
                            <Text fontSize="sm" color={mutedText}>
                              {risk.description || risk.reasoning || "Abrí el dashboard del módulo para revisar el detalle."}
                            </Text>
                          </Box>
                        ))}
                      </Stack>
                    </WamSectionCard>
                  );
                })}
              </Stack>
            ) : (
              <WamEmptyState
                title="No hay pendientes de Autopilot"
                description="Cuando WAM detecte sugerencias, aprobaciones o riesgos, van a aparecer en este panel."
              />
            )}
          </Skeleton>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};


export default WamAutopilotReviewDrawer;
